import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUpload, FiFile, FiX, FiLoader, FiClipboard, FiEdit, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';
import { useMembership } from '../../contexts/MembershipContext';
import { useBalance } from '../../contexts/BalanceContext';

const ResumeUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showTextInput, setShowTextInput] = useState(false);
  const [resumeText, setResumeText] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const { refreshCreditBalance } = useMembership();
  const { SERVICE_COSTS, creditBalance } = useBalance();
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const cost = SERVICE_COSTS['resume_upload'] || 0;
  const hasCredits = creditBalance && creditBalance.credits >= cost;

  const validateFile = useCallback((selectedFile) => {
    const validMimeTypes = [
      'application/pdf',
      'application/x-pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    const ext = (selectedFile.name || '').split('.').pop()?.toLowerCase();
    const validExtensions = ['pdf', 'docx', 'doc'];
    const hasValidMime = validMimeTypes.includes(selectedFile.type);
    const hasValidExt = validExtensions.includes(ext);

    if (!hasValidMime && !hasValidExt) {
      setError('Please upload a PDF, DOCX, or DOC file');
      return false;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return false;
    }

    setError('');
    return true;
  }, []);

  const doUpload = useCallback(async (fileToUpload) => {
    if (!hasCredits) {
      setError(`Not enough credits. Requires ${cost} credits.`);
      return;
    }

    let progressInterval;
    try {
      setUploading(true);
      setUploadProgress(0);

      progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) { clearInterval(progressInterval); return prev; }
          return prev + 10;
        });
      }, 500);

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('language', 'en');

      const response = await resumeApi.uploadResume(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadComplete(true);
      await refreshCreditBalance();

      toast.success('Resume uploaded and parsed!');
      setTimeout(() => navigate(`/resumes/${response.data.id}`), 800);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Upload failed:', err);
      if (err.response?.status === 402) {
        setError('Not enough credits. Please upgrade your plan.');
      } else {
        setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      }
      setUploadProgress(0);
      setUploadComplete(false);
    } finally {
      setUploading(false);
    }
  }, [hasCredits, cost, refreshCreditBalance, navigate]);

  const handleFileSelect = useCallback((selectedFile) => {
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      doUpload(selectedFile);
    }
  }, [validateFile, doUpload]);

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleTextUpload = async () => {
    if (!resumeText.trim()) { setError('Please enter your resume text'); return; }
    if (!hasCredits) { setError(`Not enough credits. Requires ${cost} credits.`); return; }

    let progressInterval;
    try {
      setUploading(true);
      setUploadProgress(0);

      progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) { clearInterval(progressInterval); return prev; }
          return prev + 10;
        });
      }, 500);

      const response = await resumeApi.uploadResumeText({
        text: resumeText,
        language: 'en',
        name: 'Pasted Resume',
        file_type: 'raw_text'
      });

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadComplete(true);
      await refreshCreditBalance();

      toast.success('Resume parsed successfully!');
      setTimeout(() => navigate(`/resumes/${response.data.id}`), 800);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Upload failed:', err);
      if (err.response?.status === 402) {
        setError('Not enough credits. Please upgrade your plan.');
      } else {
        setError(err.response?.data?.detail || 'Failed to parse resume text.');
      }
      setUploadProgress(0);
      setUploadComplete(false);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateBlank = async () => {
    try {
      setUploading(true);
      const response = await resumeApi.createBlankResume({ name: 'My Resume' });
      toast.success('Blank resume created!');
      navigate(`/resumes/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create resume.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {/* Title */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Add a Resume</h1>
        <p className="mt-2 text-sm text-gray-500">
          Drop your file and we'll handle the rest
        </p>
      </div>

      {/* Main Drop Zone */}
      {!showTextInput && (
        <div
          className={`relative border-2 border-dashed rounded-2xl transition-all duration-200 ${
            uploading
              ? 'border-primary-300 bg-primary-50'
              : uploadComplete
              ? 'border-green-300 bg-green-50'
              : dragActive
              ? 'border-primary-400 bg-primary-50 scale-[1.02] shadow-lg'
              : file
              ? 'border-primary-300 bg-primary-50'
              : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50 cursor-pointer'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploading && !file && fileInputRef.current?.click()}
        >
          <input
            type="file"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.docx,.doc"
          />

          <div className="flex flex-col items-center justify-center py-16 px-6">
            {uploadComplete ? (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <FiCheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-lg font-semibold text-green-700">Done! Redirecting...</p>
              </>
            ) : uploading ? (
              <>
                <FiLoader className="h-10 w-10 text-primary-500 animate-spin mb-4" />
                <p className="text-base font-medium text-gray-700">Parsing your resume...</p>
                <div className="w-64 mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">{uploadProgress}%</p>
                </div>
              </>
            ) : file ? (
              <div className="flex items-center gap-4">
                <FiFile className="h-10 w-10 text-primary-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="p-1.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                  dragActive ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  <FiUpload className={`h-7 w-7 ${dragActive ? 'text-primary-600' : 'text-gray-400'}`} />
                </div>
                <p className="text-base font-medium text-gray-700">
                  {dragActive ? 'Drop it here!' : 'Drop your resume here'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  or <span className="text-primary-600 font-medium">click to browse</span>
                </p>
                <p className="text-xs text-gray-400 mt-3">PDF, DOCX, DOC up to 5MB</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Text Input Mode */}
      {showTextInput && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">Paste resume text</h3>
            <button
              onClick={() => { setShowTextInput(false); setResumeText(''); setError(''); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back to file upload
            </button>
          </div>
          <textarea
            rows={10}
            className="block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm border border-gray-300 rounded-lg p-3"
            placeholder="Paste your resume content here..."
            value={resumeText}
            onChange={(e) => setResumeText(e.target.value)}
            disabled={uploading}
          />
          <button
            onClick={handleTextUpload}
            disabled={!resumeText.trim() || uploading || !hasCredits}
            className="mt-4 w-full inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <><FiLoader className="animate-spin mr-2 h-4 w-4" /> Parsing...</>
            ) : (
              <><FiClipboard className="mr-2 h-4 w-4" /> Parse Text ({cost} credits)</>
            )}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* Credit info */}
      {!hasCredits && !error && (
        <div className="mt-4 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
          Upload requires {cost} credits. You have {creditBalance?.credits || 0}.
        </div>
      )}

      {/* Secondary actions */}
      {!uploading && !uploadComplete && (
        <div className="mt-6 flex items-center justify-center gap-6 text-sm">
          {!showTextInput && (
            <button
              onClick={() => { setShowTextInput(true); setError(''); }}
              className="inline-flex items-center text-gray-500 hover:text-primary-600 transition-colors"
            >
              <FiClipboard className="mr-1.5 h-4 w-4" />
              Paste text instead
            </button>
          )}
          <button
            onClick={handleCreateBlank}
            className="inline-flex items-center text-gray-500 hover:text-primary-600 transition-colors"
          >
            <FiEdit className="mr-1.5 h-4 w-4" />
            Start from scratch
          </button>
          <button
            onClick={() => navigate('/resumes')}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ResumeUpload;
